import { keepPreviousData, useQuery } from '@tanstack/react-query'

import { buscarClientesGuardados } from '@/lib/campo/catalogoLocal'
import { supabase } from '@/lib/supabase/cliente'
import { mensajeDeError } from '@/lib/supabase/errores'
import type { ClienteWorldOffice, ProductoInventario } from '@/lib/supabase/tipos-bd'
import type { Orden } from '@/shared/ui/Tabla/OrdenTabla'

/**
 * Los maestros que vienen de World Office.
 *
 * Son 2.566 clientes y 2.516 productos. Traerlos completos al navegador no es
 * una lista, es un volcado: el corte y el conteo van en la consulta.
 *
 * El total se pide junto con la página —con `count: 'exact'`— para que sea el
 * total DEL FILTRO y no el de la tabla: buscar «cable» y ver «de 2.516» sería
 * mentir sobre cuánto falta por recorrer.
 */

const POR_PAGINA = 50

/** Columnas por las que se puede ordenar la lista de clientes. */
export type CampoClientes =
  | 'identificacion'
  | 'nombre'
  | 'ordenes'
  | 'cotizaciones'
  | 'ultima_orden'

export type FiltroInventario =
  | 'todos'
  | 'con-precio'
  | 'sin-precio'
  | 'usados'
  | 'agotados'
  | 'con-existencias'

/**
 * Columnas por las que se puede ordenar el inventario.
 *
 * Se ordena por 'nombre' y no por 'descripcion': la descripción cruda empieza
 * con el código del producto ('0043 - CABLE 08'), así que ordenarla de la A a
 * la Z ordena por código y el resultado parece aleatorio. Se ordena por lo
 * que la persona está leyendo.
 */
export type CampoInventario = 'nombre' | 'precio_sin_iva' | 'veces_cotizado' | 'existencias'

type Pagina<T> = {
  filas: T[]
  total: number
  cargando: boolean
  error: string | null
  porPagina: number
}

export function useClientes(
  termino: string,
  orden: Orden<CampoClientes>,
  pagina = 0,
): Pagina<ClienteWorldOffice> {
  const consulta = useQuery({
    queryKey: ['clientes-wo', termino, orden.campo, orden.ascendente, pagina],
    // Mientras carga la página nueva se conserva la anterior: sin esto la
    // tabla parpadea a vacío en cada clic y se pierde el sitio.
    placeholderData: keepPreviousData,
    queryFn: async () => {
      let peticion = supabase
        .from('vista_clientes')
        .select('*', { count: 'exact' })
        .range(pagina * POR_PAGINA, pagina * POR_PAGINA + POR_PAGINA - 1)

      const busqueda = termino.trim()
      if (busqueda) {
        // Por nombre o por documento: quien escribe «900776» busca un NIT, y
        // quien escribe «ferretería» busca un nombre.
        peticion = peticion.or(`nombre.ilike.%${busqueda}%,identificacion.ilike.%${busqueda}%`)
      }

      // El orden viaja a la consulta y no se aplica en el navegador: con
      // paginación, ordenar aquí solo ordenaría la página que se está viendo.
      peticion = peticion.order(orden.campo, {
        ascending: orden.ascendente,
        // Los que no tienen órdenes ni fecha van al final en cualquier
        // sentido: son los que menos dicen.
        nullsFirst: false,
      })

      // Desempate estable, para que dos clientes con el mismo número de
      // órdenes no cambien de sitio entre páginas.
      if (orden.campo !== 'nombre') {
        peticion = peticion.order('nombre')
      }

      const { data, error, count } = await peticion

      if (error) throw new Error(mensajeDeError(error))

      return {
        filas: (data ?? []) as unknown as ClienteWorldOffice[],
        total: count ?? 0,
      }
    },
  })

  return {
    filas: consulta.data?.filas ?? [],
    total: consulta.data?.total ?? 0,
    cargando: consulta.isLoading,
    error: consulta.error?.message ?? null,
    porPagina: POR_PAGINA,
  }
}

export function useInventario(
  termino: string,
  filtro: FiltroInventario,
  orden: Orden<CampoInventario>,
  pagina = 0,
): Pagina<ProductoInventario> {
  const consulta = useQuery({
    queryKey: ['inventario', termino, filtro, orden.campo, orden.ascendente, pagina],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      let peticion = supabase
        .from('vista_inventario')
        .select('*', { count: 'exact' })
        .range(pagina * POR_PAGINA, pagina * POR_PAGINA + POR_PAGINA - 1)

      const busqueda = termino.trim()
      if (busqueda) {
        peticion = peticion.ilike('descripcion', `%${busqueda}%`)
      }

      if (filtro === 'con-precio') peticion = peticion.eq('precio_a_convenir', false)
      if (filtro === 'sin-precio') peticion = peticion.eq('precio_a_convenir', true)
      if (filtro === 'usados') peticion = peticion.gt('veces_cotizado', 0)

      // «Agotado» es lo que se inventaría y no queda: la vista ya distingue
      // eso de lo que sencillamente no se guarda en una bodega, que en cero
      // no significa nada.
      if (filtro === 'agotados') peticion = peticion.eq('agotado', true)
      if (filtro === 'con-existencias') peticion = peticion.gt('existencias', 0)

      // Los dos productos cuyo nombre es una fila de comas van siempre al
      // final: ordenar de la A a la Z los ponía primero, porque los signos
      // preceden a las letras, y lo primero que se veía era basura.
      peticion = peticion.order('sin_nombre', { ascending: true })

      peticion = peticion.order(orden.campo, {
        ascending: orden.ascendente,
        nullsFirst: false,
      })

      if (orden.campo !== 'nombre') {
        peticion = peticion.order('nombre')
      }

      const { data, error, count } = await peticion

      if (error) throw new Error(mensajeDeError(error))

      return {
        filas: (data ?? []) as unknown as ProductoInventario[],
        total: count ?? 0,
      }
    },
  })

  return {
    filas: consulta.data?.filas ?? [],
    total: consulta.data?.total ?? 0,
    cargando: consulta.isLoading,
    error: consulta.error?.message ?? null,
    porPagina: POR_PAGINA,
  }
}

/**
 * Búsqueda corta de clientes, para los selectores.
 *
 * Sin paginación a propósito: en un desplegable no se navega, se escribe hasta
 * que aparece el que se busca.
 */
export function useBuscarClientes(termino: string) {
  const consulta = useQuery({
    queryKey: ['clientes-wo', 'buscar', termino],
    networkMode: 'offlineFirst',
    queryFn: async (): Promise<ClienteWorldOffice[]> => {
      /*
       * SIN SEÑAL, LOS CLIENTES GUARDADOS EN EL TELÉFONO.
       *
       * Levantar una obra en la calle exige escoger el cliente, y sin esta
       * lista el formulario se planta en «Falta el cliente» y la visita se
       * pierde. Se descargan con el catálogo de materiales.
       */
      if (!navigator.onLine) {
        return (await buscarClientesGuardados(termino)) as unknown as ClienteWorldOffice[]
      }

      // 24 y no 12: con la búsqueda por palabras el resultado ya viene
      // acotado, y un techo bajo dejaba fuera clientes que sí coinciden solo
      // por tener pocas cotizaciones. Ese era el «no aparece» que se reportó.
      let peticion = supabase.from('vista_clientes').select('*').limit(24)

      const busqueda = termino.trim()
      if (busqueda) {
        // Antes se buscaba la frase entera como un solo trozo: «ferretería
        // sol» no encontraba «FERRETERÍA EL SOL» porque «el» queda en medio.
        // Ahora cada palabra se exige por separado —y en cualquier orden—:
        // cada `.or()` es un grupo, y varios `.or()` se combinan con Y. Así
        // «sol ferretería» y «ferretería sol» encuentran lo mismo.
        const palabras = busqueda
          .split(/\s+/)
          .map((p) => p.replace(/[,()]/g, ''))
          .filter(Boolean)
        for (const palabra of palabras) {
          peticion = peticion.or(`nombre.ilike.%${palabra}%,identificacion.ilike.%${palabra}%`)
        }
      }

      const { data, error } = await peticion
        .order('cotizaciones', { ascending: false })
        .order('nombre')

      if (error) throw new Error(mensajeDeError(error))

      return (data ?? []) as unknown as ClienteWorldOffice[]
    },
  })

  return { clientes: consulta.data ?? [], cargando: consulta.isFetching }
}
